// Document types the builder can design for. `key` is the print_preferences /
// custom_invoice type used by the backend. Field lists mirror exactly what the
// backend print endpoints put in orderData / itemData / itemBatchData.

const BILLING_ITEM = [
  "item_id", "item_code", "item_name", "item_generic_name", "item_pack_size", "item_batch_number",
  "item_quantity", "item_free_quantity", "item_exp_date", "item_mfg_date", "item_rack_location",
  "item_unit_price", "item_schedule", "item_discount_amount", "item_discount_percentage",
  "item_tax_amount", "item_total_tax_percentage", "item_total_amount", "item_price_wiithout_tax",
  "item_gst", "item_sgst", "item_cgst", "item_igst", "item_uom", "item_manufacturer_name",
  "item_mfg", "item_mfg_code", "item_hsn",
];

const SALES_ITEM = [
  "item_id", "item_code", "item_name", "item_generic_name", "item_pack_size", "item_rack_location",
  "item_unit_price", "item_quantity", "item_free_quantity", "item_schedule", "item_discount_amount",
  "item_discount_percentage", "item_tax_amount", "item_batch_number", "item_exp_date",
  "item_total_tax_percentage", "item_total_amount", "item_gst", "item_sgst", "item_cgst", "item_igst",
  "item_uom", "item_hsn", "item_manufacturer_name",
];

const SALES_BATCH = [
  "item_batch_number", "item_batch_quantity", "item_exp_date", "item_batch_purchase_rate",
  "item_batch_unit_price", "from_bin_location", "item_batch_sales_rate", "item_batch_free_quantity",
  "item_batch_discount_amount", "item_batch_discount_percentage", "item_batch_tax_percentage",
  "item_batch_tax_amount", "item_batch_total_sales_rate", "item_batch_final_sales_rate",
];

const PURCHASE_ITEM = [
  "item_id", "item_code", "item_name", "item_generic_name", "item_pack_size", "item_rack_location",
  "item_unit_price", "item_quantity", "item_free_quantity", "item_schedule", "item_discount_amount",
  "item_discount_percentage", "item_tax_amount", "item_total_tax_percentage", "item_total_amount",
  "item_uom", "item_gst", "item_sgst", "item_cgst", "item_igst", "item_hsn", "item_manufacturer_name",
  "base_quantity",
];

const PURCHASE_BATCH = [
  "item_batch_number", "item_sellable_quantity", "item_non_sellable_quantity", "item_exp_date",
  "item_mfg_date", "item_batch_quantity", "item_batch_free_quantity", "item_batch_unit_price",
  "item_batch_purchase_rate", "item_batch_discount_percentage", "item_batch_discount_amount",
  "item_batch_tax_amount", "item_batch_tax_percentage", "item_batch_final_purchase_rate",
  "item_batch_total_purchase_rate", "to_bin_location",
];

const STORE_FIELDS = [
  "company", "company_alias_name", "company_address", "company_area", "company_landmark", "company_city",
  "company_state", "company_pincode", "company_email", "company_telephone1", "company_telephone2",
  "company_gstn", "company_licence", "company_logo", "pan_number", "store_code", "dl_no_20_20b",
  "dl_no_21_21b", "dl_no_21c", "dl_no_21f", "fssai_no", "company_remarks", "company_terms_conditions",
  "company_return_policy",
];

// p = column prefix used by the document (sot_, sott_, sodtt_ ...)
function partyOrder(p, extra = []) {
  return [
    `${p}_id`, "cmr_code", "cmr_name", "cmr_phone_number", `${p}_billing_address`, `${p}_ship_to_address`,
    `${p}_pay_to_address`, `${p}_gst_number`, `${p}_sub_total`, `${p}_total_discount`, `${p}_total_gst`,
    `${p}_round_off_value`, `${p}_total_amount`, `${p}_payment_status`, `${p}_payment_method`,
    `${p}_order_status`, `${p}_document_date`, `${p}_delivery_date`, `${p}_due_date`,
    `${p}_reference_inv_number`, `${p}_reference_inv_date`, "gst_type", "remarks", "created_date",
    ...extra,
  ];
}

const TCS = ["tds_tcs_type", "tcs_or_tds_percentage", "tcs_or_tds_amount"];

export const DOC_TYPES = [
  {
    key: "billing_invoice",
    label: "Billing Invoice",
    group: "Billing",
    prefix: "sot",
    numberField: "sot_invoice_number",
    party: "customer",
    order: [
      "sot_id", "sot_invoice_number", "invoice_number", "created_date", "cmr_first_name", "cmr_last_name",
      "cmr_phone_number", "cmr_company", "cmr_gst", "cmr_pan", "cmr_state", "cmr_area", "cmr_rewards",
      "doctor_name", "sot_billing_address", "sot_payment_method", "sot_payment_status", "sot_transaction_id",
      "sot_sub_total", "sot_total_discount", "sot_total_gst", "round_off", "sot_total_amount", "sot_remarks",
    ],
    item: BILLING_ITEM,
    payments: true,
  },
  {
    key: "billing_return",
    label: "Billing Return (Credit Note)",
    group: "Billing",
    prefix: "icn",
    numberField: "icn_invoice_number",
    party: "customer",
    order: [
      "icn_id", "icn_invoice_number", "invoice_number", "created_date", "cmr_first_name", "cmr_last_name",
      "cmr_phone_number", "cmr_rewards", "doctor_name", "icn_billing_address", "icn_payment_method",
      "icn_payment_status", "icn_transaction_id", "icn_sub_total", "icn_total_discount", "icn_total_gst",
      "round_off_value", "icn_total_amount", "icn_remarks",
    ],
    item: [...BILLING_ITEM.filter((k) => !["item_mfg", "item_mfg_code"].includes(k)), "linked_invoice_number", "sot_invoice_number"],
    payments: true,
  },
  {
    key: "sales_order",
    label: "Sales Order",
    group: "Sales",
    prefix: "sott",
    numberField: "sott_invoice_number",
    party: "customer",
    order: partyOrder("sott", ["sott_invoice_number"]),
    item: [...SALES_ITEM.filter((k) => !["item_batch_number", "item_exp_date", "item_free_quantity"].includes(k)), "item_to_be_delivered"],
  },
  {
    key: "delivery_note",
    label: "Delivery Note",
    group: "Sales",
    prefix: "sodtt",
    numberField: "inv_no",
    party: "customer",
    order: partyOrder("sodtt", ["inv_no", "document_date", ...TCS, "sodtt_invoice_discount"]),
    item: SALES_ITEM,
    batch: SALES_BATCH,
  },
  {
    key: "sales_invoice",
    label: "Sales Invoice",
    group: "Sales",
    prefix: "soit",
    numberField: "inv_no",
    party: "customer",
    order: partyOrder("soit", ["inv_no", "soit_custom_invoice_number", "document_date", ...TCS, "soit_invoice_discount"]),
    item: SALES_ITEM,
    batch: SALES_BATCH,
  },
  {
    key: "sales_return",
    label: "Sales Return",
    group: "Sales",
    prefix: "srt",
    numberField: "srt_invoice_number",
    party: "customer",
    order: partyOrder("srt", ["srt_invoice_number", ...TCS, "srt_invoice_discount"]),
    item: SALES_ITEM,
    batch: SALES_BATCH,
  },
  {
    key: "sales_credit_note",
    label: "Sales Credit Note",
    group: "Sales",
    prefix: "sct",
    numberField: "sct_custom_invoice_number",
    party: "customer",
    order: partyOrder("sct", ["sct_custom_invoice_number", "sct_order_date", ...TCS, "sct_invoice_discount"]),
    item: SALES_ITEM,
    batch: SALES_BATCH,
  },
  {
    key: "purchase_order",
    label: "Purchase Order",
    group: "Purchase",
    prefix: "pot",
    numberField: "inv_no",
    party: "vendor",
    order: partyOrder("pot", ["inv_no", "customer_invoice_number", "pot_expected_delivery_date", "created_by", "cmr_gst"]),
    item: [...PURCHASE_ITEM.filter((k) => k !== "base_quantity" && k !== "item_free_quantity"), "mrp", "molecule_name", "item_to_be_received"],
  },
  {
    key: "good_receipt_note",
    label: "Goods Receipt Note (GRN)",
    group: "Purchase",
    prefix: "gort",
    numberField: "inv_no",
    party: "vendor",
    order: partyOrder("gort", ["inv_no", "customer_invoice_number", "document_date", ...TCS, "gort_invoice_discount", "pcns_total", "remaining_balance"]),
    item: [...PURCHASE_ITEM, "item_received_quantity"],
    batch: [...PURCHASE_BATCH, "item_batch_margin_percentage", "item_batch_taxable_amount"],
    creditNotes: true,
  },
  {
    key: "purchase_invoice",
    label: "Purchase Invoice",
    group: "Purchase",
    prefix: "poit",
    numberField: "inv_no",
    party: "vendor",
    order: partyOrder("poit", ["inv_no", "customer_invoice_number", "poit_custom_invoice_number", "poit_order_date", "document_date", ...TCS, "poit_invoice_discount", "pcns_total", "remaining_balance"]),
    item: PURCHASE_ITEM,
    batch: PURCHASE_BATCH,
    creditNotes: true,
  },
  {
    key: "purchase_return",
    label: "Purchase Return",
    group: "Purchase",
    prefix: "prt",
    numberField: "prt_invoice_number",
    party: "vendor",
    order: partyOrder("prt", ["prt_invoice_number", "customer_invoice_number", ...TCS, "prt_invoice_discount"]),
    item: PURCHASE_ITEM,
    batch: PURCHASE_BATCH,
  },
  {
    key: "purchase_credit_note",
    label: "Purchase Credit / Debit Note",
    group: "Purchase",
    prefix: "pct",
    numberField: "pct_custom_invoice_number",
    party: "vendor",
    order: partyOrder("pct", ["pct_custom_invoice_number", "customer_invoice_number", ...TCS, "pct_invoice_discount"]),
    item: PURCHASE_ITEM,
    batch: [...PURCHASE_BATCH, "item_batch_cgst_amount", "item_batch_sgst_amount", "item_batch_igst_amount"],
  },
  {
    key: "stock_transfer",
    label: "Stock Transfer",
    group: "Inventory",
    prefix: "itt",
    numberField: "stock_transfer_doc_number",
    party: "none",
    order: [
      "itt_id", "stock_transfer_doc_number", "transfer_date", "posting_date", "stock_management_action",
      "from_warehouse_id", "from_warehouse_name", "to_warehouse_id", "to_warehouse_name",
      "stock_transfer_reason", "stock_transfer_status",
    ],
    item: ["item_id", "item_code", "item_name", "item_uom", "item_uom_source", "item_uom_destination", "item_quantity"],
    batch: [
      "item_batch_number", "item_exp_date", "from_bin_location", "to_bin_location", "from_warehouse_name",
      "to_warehouse_name", "item_batch_quantity", "item_batch_free_quantity", "item_batch_unit_price",
      "item_batch_purchase_rate",
    ],
    extra: {
      fromStore: ["store_code", "firm_name", "firm_address", "firm_area", "firm_city", "firm_state", "firm_pin_code"],
      toStore: ["store_code", "firm_name", "firm_address", "firm_area", "firm_city", "firm_state", "firm_pin_code"],
    },
  },
];

export const STORE_FIELD_LIST = STORE_FIELDS;
export const PAYMENT_FIELDS = ["payment_mode_name", "payment_amount"];
export const TAX_FIELDS = ["item_gst", "taxable", "gst", "sgst", "igst"];
export const CREDIT_NOTE_FIELDS = ["invoice_number", "invoice_date", "invoice_total_amount", "claimed_amount", "adjusted_amount", "remaining_amount", "remarks"];

export const DOC_TYPE_MAP = DOC_TYPES.reduce((m, d) => {
  m[d.key] = d;
  return m;
}, {});

export function getDocType(key) {
  return DOC_TYPE_MAP[key] || DOC_TYPES[0];
}

const LABEL_OVERRIDES = {
  cmr_name: "Party Name",
  cmr_code: "Party Code",
  cmr_first_name: "Customer First Name",
  cmr_last_name: "Customer Last Name",
  cmr_phone_number: "Party Phone",
  cmr_gst: "Party GSTIN",
  cmr_pan: "Customer PAN",
  cmr_area: "Customer Address",
  inv_no: "Document No.",
  invoice_number: "Invoice No.",
  round_off: "Round Off",
  company: "Company Name",
  company_gstn: "Company GSTIN",
  company_licence: "Licence No.",
  dl_no_20_20b: "DL No. 20/20B",
  dl_no_21_21b: "DL No. 21/21B",
  dl_no_21c: "DL No. 21C",
  dl_no_21f: "DL No. 21F",
  fssai_no: "FSSAI No.",
  item_price_wiithout_tax: "Price Without Tax",
  item_hsn: "HSN / SAC",
  item_gst: "GST %",
  item_cgst: "CGST %",
  item_sgst: "SGST %",
  item_igst: "IGST %",
  item_uom: "UOM",
  item_mfg: "Manufacturer",
  item_mfg_code: "Mfr. Code",
  gst: "CGST Amount",
  sgst: "SGST Amount",
  igst: "IGST Amount",
  taxable: "Taxable Value",
};

export function fieldLabel(key, prefix) {
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
  let k = key;
  if (prefix && k.startsWith(prefix + "_")) k = k.slice(prefix.length + 1);
  k = k.replace(/^item_batch_/, "batch_").replace(/^item_/, "").replace(/^company_/, "company ");
  return k
    .split("_")
    .filter(Boolean)
    .map((w) => (["gst", "id", "pan", "uom", "tds", "tcs", "dl"].includes(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}
